import { useEffect, useState } from "react";
import DocumentActions from "./DocumentActions";
import { getFileType, isImage } from "./DocumentCompFunctions";
import { useDocument } from "./DocumentComponentContext";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import Button from "../../atomic/Button";
import { notify } from "../../../../../src/shared/context/AlertContext";
import DialogTwo from "../../atomic/Dialog/DialogTwo";
import DialogOne from "../../atomic/Dialog/DialogOne";
import PDFViewer from "../PDFViewer";
import DocumentInformation from "./DocumentInformation";
import DocumentScreening from "./DocumentScreening";
import UploadedDocumentView from "../UploadedDocumentView";
import AddNewDocumentSlider from "../../../pages/AllDocuments/AddNewDocumentSlider";
import { CollapsibleSidebar } from "../../atomic/CollapsibleSidebar";
import { getUserRbacGroup } from "../../../shared/utils/Global";

interface DocumentScreeningOptions {
  [key: string]: string;
}

const METADATA_LABELS: Record<string, string> = {
  date: "Date",
  details: "Details",
  originalXerox: "Original/Xerox",
  latitude: "Latitude",
  longitude: "Longitude",
  isOverlay: "Is Overlay"
};

interface DocumentComponentProps {
  showEditDocument: boolean;
  allowDelete?: boolean;
  showDownloadDocument: boolean;
  documentName: string;
  documentType: string;
  documentUrl: {
    backImagePath: string;
    frontImagePath: string;
    frontFileName?: string;
    backFileName?: string;
  };
  documentID: string;
  mediaID?: string;
  documentPassword: string;
  pageName: string;
  permissions: any;
  isLenderDashboard: boolean;
  token: string;
  rejectionReason: string;
  documentStatus: string;
  loanID: string;
  isAdditionalDocs: boolean;
  coApplicantKYCDetailsID: string;
  isCoApplicant: boolean;
  isBackImage: boolean;
  loanKycDetailsID: string;
  loanDetails: any;
  reloadParent?: () => void;
  isMFL_LAP: boolean;
  tabPermissions: any;
  docID: string;
  createdAt: string;
  documentScreeningOptions?: DocumentScreeningOptions;
  displayNameInConfig: string;
  mediaMetaData?: {
    date?: string;
    details?: string;
    originalXerox?: string;
    latitude?: string;
    longitude?: string;
    isOverlay?: string;
    [key: string]: any;
  };
  isCompact?: boolean;
  allowDownload?: boolean;
}

const DocumentComponent = (documentProp: DocumentComponentProps) => {
  const { document, updateDocument } = useDocument();

  const [pdfViewModalOpen, setPdfViewModalOpen] = useState(false);
  const [objectPath, setObjectPath] = useState<string | null>(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [showEditMetadataSlider, setShowEditMetadataSlider] = useState(false);
  const [showVideoViewModal, setShowVideoViewModal] = useState(false);
  const rbacGroupName = getUserRbacGroup();

  const isBCMUser = rbacGroupName === "BCM Group";
  const isCOUser = rbacGroupName === "CO Group";

  const isLoanSanctioned =
    documentProp.loanDetails?.taskStatus?.toLowerCase() === "loan_sanction";

  const showEditButton = isBCMUser || (isCOUser && isLoanSanctioned);

  const getImagePath = () =>
    documentProp.isBackImage
      ? documentProp.documentUrl.backImagePath
      : documentProp.documentUrl.frontImagePath;

  useEffect(() => {
    updateDocument({
      loanDetails: documentProp.loanDetails,
      documentID: documentProp.documentID,
      mediaID: documentProp.mediaID,
      docID: documentProp.docID,
      documentStatus: documentProp.documentStatus,
      rejectionReason: documentProp.rejectionReason,
      isAdditionalDocs: documentProp.isAdditionalDocs || true,
      loanID: documentProp.loanID,
      isCoApplicant: documentProp.isCoApplicant,
      loanKycDetailsID: documentProp.loanKycDetailsID,
      coApplicantKYCDetailsID: documentProp.coApplicantKYCDetailsID,
      pageName: documentProp.pageName,
      token: documentProp.token,
      showEditDocument: documentProp.showEditDocument,
      allowDelete: documentProp.allowDelete,
      isBackImage: documentProp.isBackImage,
      showDownloadDocument: documentProp.showDownloadDocument,
      documentName: documentProp.documentName,
      documentType: documentProp.documentType,
      documentUrl: {
        backImagePath: documentProp.documentUrl.backImagePath,
        frontImagePath: documentProp.documentUrl.frontImagePath,
        frontFileName: documentProp.documentUrl.frontFileName,
        backFileName: documentProp.documentUrl.backFileName
      },
      documentPassword: documentProp.documentPassword,
      permissions: documentProp.permissions,
      isLenderDashboard: documentProp.isLenderDashboard,
      tabPermissions: documentProp.tabPermissions,
      documentScreeningOptions: documentProp.documentScreeningOptions,
      createdAt: documentProp.createdAt,
      displayNameInConfig: documentProp.displayNameInConfig,
      isCompact: documentProp.isCompact
    });
  }, [documentProp]);

  function handlePdfView() {
    if (getFileType(getImagePath()) === "video") {
      setShowVideoViewModal(true);
      return;
    }

    if (!getImagePath()) {
      notify({
        title: "Error",
        type: "error",
        text: "Sorry, the documentProp you are looking for does not exist. Please check the URL or try again later."
      });

      return;
    }

    setPdfViewModalOpen(true);
  }

  function handleImageClick(imagePath: string) {
    setObjectPath(imagePath);
  }

  function handlePdfButtonClick() {
    if (getImagePath()) {
      setPdfViewModalOpen(true);
    } else {
      notify({
        title: "Error",
        type: "error",
        text: "Sorry, the document you are looking for does not exist. Please check the URL or try again later."
      });
    }
  }

  const renderDocumentPreviewContent = (
    onImageClick: (path: string) => void,
    onPdfClick: () => void
  ) => {
    return (
      <>
        {isImage(getImagePath()) ? (
          <img
            src={getImagePath() || ""}
            alt={documentProp?.documentName || ""}
            className="flex-grow object-cover h-full w-full cursor-pointer"
            onClick={() => onImageClick(getImagePath() || "")}
          />
        ) : document?.uploadedDocument ? (
          <div className="absolute bottom-[40px] w-full py-1 flex items-center justify-center">
            <Button className="mr-4" onClick={onCancelImage}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => updateDocument({ callSaveAction: true })}
            >
              Save
            </Button>
          </div>
        ) : (
          <Button
            onClick={onPdfClick}
            className="flex-grow w-full bg-white flex align-center flex-col justify-center hover:!bg-slate-300 items-center h-full text-gray-700 font-semibold"
          >
            <DocumentTextIcon
              className={documentProp.isCompact ? "h-6 w-6" : "h-10 w-10"}
            />
            <span className={documentProp.isCompact ? "text-[11px]" : ""}>
              Click to view
            </span>
          </Button>
        )}
      </>
    );
  };

  function onCancelImage() {
    updateDocument({ callSaveAction: false, uploadedDocument: null });
  }

  useEffect(() => {
    if (document.reloadParent) {
      documentProp?.reloadParent?.();

      updateDocument({ reloadParent: false });
    }
  }, [document.reloadParent]);

  return (
    <div
      id="documentComponent"
      className={
        documentProp.isCompact ? "w-full p-2" : "w-full md:w-1/3 p-2 mb-10"
      }
    >
      <div
        className={`relative w-full flex items-center justify-center border border-gray-200 rounded-lg bg-gray-50 overflow-hidden ${
          documentProp.isCompact ? "h-[130px]" : "h-[250px]"
        }`}
      >
        {renderDocumentPreviewContent(handleImageClick, handlePdfView)}
        <DocumentActions
          isMFL_LAP={documentProp?.isMFL_LAP}
          reloadParent={documentProp?.reloadParent}
          allowDownload={documentProp?.allowDownload}
        />

        {document.tabPermissions?.["features"]?.["SCREENING_STATUS_BADGE"]
          ?.isVisible && <DocumentScreening />}
      </div>

      <DocumentInformation />

      {documentProp?.mediaMetaData &&
        Object.keys(documentProp?.mediaMetaData)?.length > 0 && (
          <div className="mt-2 flex justify-center">
            <Button
              color="primary"
              onClick={() => setShowMetadataModal(true)}
              className={
                documentProp.isCompact ? "text-[10px] !py-1 !px-2" : "text-xs"
              }
            >
              View metadata
            </Button>
          </div>
        )}

      <DialogOne
        open={showMetadataModal}
        setOpen={setShowMetadataModal}
        title="Document Metadata"
        styles={{
          panel: "!w-[500px] max-w-[90vw]"
        }}
      >
        <div className="p-4">
          <div className="mb-4">
            <div className="relative flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white">
              <div className="relative w-full h-[250px] flex items-center justify-center bg-gray-50 overflow-hidden">
                {renderDocumentPreviewContent(
                  handleImageClick,
                  handlePdfButtonClick
                )}
              </div>
            </div>
          </div>

          {documentProp?.mediaMetaData &&
            Object.keys(documentProp?.mediaMetaData)?.length > 0 && (
              <div className="space-y-3">
                {Object.entries(documentProp.mediaMetaData)
                  .filter(
                    ([key, value]) =>
                      value !== null &&
                      value !== undefined &&
                      key !== "isOverlay"
                  )
                  .map(([key, value]) => {
                    const label =
                      METADATA_LABELS[key] ||
                      key.charAt(0).toUpperCase() + key.slice(1);
                    const isDetailsField = key === "details";

                    return (
                      <div
                        key={key}
                        className="flex justify-between items-start border-b pb-2"
                      >
                        <span className="font-medium text-gray-700 text-sm">
                          {label}:
                        </span>
                        <span
                          className={`text-gray-600 text-sm text-right ${
                            isDetailsField ? "max-w-[60%] break-words" : ""
                          }`}
                        >
                          {String(value || "-")}
                        </span>
                      </div>
                    );
                  })}
              </div>
            )}

          {showEditButton && (
            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => {
                  setShowMetadataModal(false);
                  setShowEditMetadataSlider(true);
                }}
                className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition"
              >
                Edit
              </Button>
            </div>
          )}
        </div>
      </DialogOne>

      <DialogTwo
        open={showVideoViewModal}
        setOpen={setShowVideoViewModal}
        styles={{
          panel: "!bg-transparent !p-0 max-w-fit md:min-w-max shadow-none"
        }}
      >
        <video width="600" controls>
          <source src={getImagePath()} type="video/mp4" />
          <track kind="captions" />
          Your browser does not support the video tag.
        </video>
      </DialogTwo>

      <DialogTwo
        open={pdfViewModalOpen}
        setOpen={setPdfViewModalOpen}
        styles={{
          panel: "!bg-transparent !p-0 max-w-fit md:min-w-max shadow-none"
        }}
      >
        <PDFViewer
          isOpen={pdfViewModalOpen}
          pdfUrl={getImagePath()}
          name={documentProp?.documentName?.replace("_", " ")}
          style={{ title: "text-white", pageCount: "text-white" }}
          onClose={() => setPdfViewModalOpen(false)}
        />
      </DialogTwo>

      <UploadedDocumentView
        objectPath={objectPath}
        onClose={() => setObjectPath(null)}
      />

      <CollapsibleSidebar
        show={showEditMetadataSlider}
        setShow={setShowEditMetadataSlider}
      >
        <AddNewDocumentSlider
          isMFL_LAP={documentProp.isMFL_LAP}
          pageName={documentProp.pageName}
          isOpen={showEditMetadataSlider}
          studioExperience={false}
          token={documentProp.token}
          close={() => setShowEditMetadataSlider(false)}
          setCategory={() => {}}
          reloadParent={() => {
            documentProp?.reloadParent?.();
            setShowMetadataModal(false);
          }}
          isLenderDashboard={documentProp.isLenderDashboard}
          useParams={() => ({ loanID: documentProp.loanID })}
          location={{ pathname: "" }}
          notify={notify}
          userID={documentProp.loanDetails?.userID}
          sourceEntityID={undefined}
          loanDetails={documentProp.loanDetails}
          editMode={true}
          editModeMetadata={documentProp.mediaMetaData}
          editModeMediaID={documentProp.mediaID}
        />
      </CollapsibleSidebar>
    </div>
  );
};

export default DocumentComponent;
